/**
 * TOC Heading Links
 *
 * Automatically generates table of contents from H2 headings in rich text.
 * Creates links with smooth scrolling and active state tracking.
 *
 * Features:
 * - Automatically adds IDs to H2 headings based on text content
 * - Generates numbered TOC entries from template item
 * - Active state tracking with IntersectionObserver (25% from top trigger)
 * - Smooth scroll handled by smooth-scroll module
 * - Full cleanup on destroy (removes IDs, observers, handlers)
 */

import globalConfig from '@config';
import { querySelectorAll } from '@utils';

export function init(config) {
  const activeClass = globalConfig._global.classes.active;
  const cleanup = {
    observers: [],
    handlers: [],
    scrollTimeout: null,
    headings: [],
  };

  const addObserver = (observer: IntersectionObserver) => cleanup.observers.push(observer);
  const addHandler = (element: Element | Window, event: string, handler: EventListener) => {
    element.addEventListener(event, handler);
    cleanup.handlers.push({ element, event, handler });
  };

  const contentAreas = querySelectorAll(config, 'content');
  const tocLists = querySelectorAll(config, 'list');

  contentAreas.forEach((contentArea) => {
    // Since there's only 1 content area and 1 TOC list per page, use the first TOC list
    const tocList = tocLists[0];

    if (!tocList || tocList.children.length === 0) {
      return;
    }

    const template = tocList.children[0].cloneNode(true) as HTMLElement;
    const templateLink = template.querySelector('a');
    if (templateLink) {
      templateLink.classList.remove(activeClass);
    }

    // Clear all original TOC items
    tocList.innerHTML = '';

    const h2Headings = contentArea.querySelectorAll('h2');

    // Add IDs directly to H2 headings
    h2Headings.forEach((heading) => {
      const sectionId = heading.textContent
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      heading.id = sectionId;
      cleanup.headings.push(heading);
    });

    // Create TOC entries
    h2Headings.forEach((heading, index) => {
      const tocItem = template.cloneNode(true) as HTMLElement;
      const link = tocItem.querySelector('a');
      const sectionId = heading.id;
      link.href = `#${sectionId}`;

      // Bold numbered text
      const number = document.createElement('strong');
      number.textContent = `${index + 1}. `;

      // Clear the link and add the number + text
      link.innerHTML = '';
      link.appendChild(number);
      link.appendChild(document.createTextNode(heading.textContent));

      tocList.appendChild(tocItem);
    });

    // Set up IntersectionObserver for active state
    const tocLinks = tocList.querySelectorAll('a');
    let currentActive = null;

    const updateActiveLink = () => {
      const windowHeight = window.innerHeight;
      const trigger = windowHeight * 0.25; // 25% from top (natural reading position)

      let newActive = null;

      // Find the last heading whose top is above the trigger point
      for (let i = h2Headings.length - 1; i >= 0; i--) {
        const rect = h2Headings[i].getBoundingClientRect();
        if (rect.top <= trigger) {
          newActive = h2Headings[i].id;
          break;
        }
      }

      // Only update if active section changed
      if (newActive !== currentActive) {
        currentActive = newActive;

        // Remove all active classes
        tocLinks.forEach((link) => link.classList.remove(activeClass));

        // Add to current
        if (currentActive) {
          const activeLink = Array.from(tocLinks).find(
            (link) => link.getAttribute('href') === `#${currentActive}`
          );
          if (activeLink) {
            activeLink.classList.add(activeClass);
          }
        }
      }
    };

    const observerOptions = {
      rootMargin: '-25% 0px -75% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver(() => {
      updateActiveLink();
    }, observerOptions);

    // Observe all headings
    h2Headings.forEach((heading) => observer.observe(heading));
    addObserver(observer);

    // Also update on scroll for smoother tracking
    const scrollHandler = () => {
      if (cleanup.scrollTimeout) clearTimeout(cleanup.scrollTimeout);
      cleanup.scrollTimeout = setTimeout(updateActiveLink, 50);
    };
    addHandler(window, 'scroll', scrollHandler);
  });

  return {
    result: 'toc initialized',
    destroy: () => {
      // Remove IDs from headings
      cleanup.headings.forEach((heading) => {
        heading.removeAttribute('id');
      });
      cleanup.headings.length = 0;

      // Disconnect all observers
      cleanup.observers.forEach((obs) => obs.disconnect());
      cleanup.observers.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      cleanup.handlers.length = 0;

      // Clear scroll timeout
      if (cleanup.scrollTimeout) {
        clearTimeout(cleanup.scrollTimeout);
        cleanup.scrollTimeout = null;
      }
    },
  };
}
